import { AlertCircle, AlertTriangle, XCircle, RefreshCw, Home, LucideIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Heading, Text } from '../ui/Typography';
import { Alert, AlertDescription } from '../ui/Alert';
import { cn } from '../../utils/cn';

interface ErrorStateProps {
  title?: string;
  message?: string;
  type?: 'error' | 'warning' | 'info';
  fullScreen?: boolean;
  onRetry?: () => void;
  onGoHome?: () => void;
  icon?: LucideIcon;
  showHomeButton?: boolean;
}

export function ErrorState({
  title,
  message = 'An unexpected error occurred. Please try again.',
  type = 'error',
  fullScreen = false,
  onRetry,
  onGoHome,
  icon,
  showHomeButton = false,
}: ErrorStateProps) {
  // Determine icon based on type
  const Icon = icon || (type === 'error' ? XCircle : type === 'warning' ? AlertTriangle : AlertCircle);
  
  const variantMap = {
    error: 'destructive' as const,
    warning: 'warning' as const,
    info: 'default' as const,
  };
  
  const defaultTitle = type === 'error' 
    ? 'Error'
    : type === 'warning' 
    ? 'Warning' 
    : 'Information';

  const containerClasses = fullScreen
    ? 'h-screen flex items-center justify-center'
    : 'flex items-center justify-center py-12';

  const iconBgClass = type === 'error'
    ? 'bg-red-50 dark:bg-red-900/20'
    : type === 'warning'
    ? 'bg-yellow-50 dark:bg-yellow-900/20'
    : 'bg-accent-50 dark:bg-accent-900/20';
  
  const iconColorClass = type === 'error'
    ? 'text-error'
    : type === 'warning'
    ? 'text-warning'
    : 'text-accent-500';

  return (
    <div 
      className={`${containerClasses} ${fullScreen ? 'bg-base' : ''}`}
    >
      <div className="text-center max-w-md px-6">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${iconBgClass}`}>
          <Icon className={`w-10 h-10 ${iconColorClass}`} />
        </div>
        
        <Heading level={2} className="mb-3">
          {title || defaultTitle}
        </Heading>
        
        <Text className="mb-6 text-secondary">
          {message}
        </Text>

        <div className="flex gap-3 justify-center">
          {onRetry && (
            <Button
              variant="default"
              onClick={onRetry}
              size="lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          )}
          
          {(showHomeButton || onGoHome) && (
            <Button
              variant="secondary"
              onClick={onGoHome || (() => window.location.href = '/')}
              size="lg"
            >
              <Home className="w-4 h-4 mr-2" />
              Go Home
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

interface EmptyStateProps {
  title?: string;
  message?: string;
  icon?: LucideIcon;
  actionLabel?: string;
  onAction?: () => void;
}

export function EmptyState({
  title = 'No Data',
  message = 'There is no data to display at the moment.',
  icon: Icon = AlertCircle,
  actionLabel,
  onAction,
}: EmptyStateProps) {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="text-center max-w-md px-6">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-subtle">
          <Icon className="w-10 h-10 text-muted" />
        </div>
        
        <Heading level={3} className="mb-2">
          {title}
        </Heading>
        
        <Text className="mb-6 text-secondary">
          {message}
        </Text>

        {actionLabel && onAction && (
          <Button
            variant="default"
            onClick={onAction}
            size="lg"
          >
            {actionLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

interface InlineErrorProps {
  message: string;
  onDismiss?: () => void;
}

export function InlineError({ message, onDismiss }: InlineErrorProps) {
  return (
    <Alert variant="destructive" className="flex items-start gap-3">
      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <AlertDescription className="flex-1">{message}</AlertDescription>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 transition-base text-error hover:opacity-80"
          aria-label="Dismiss"
        >
          <XCircle className="w-5 h-5" />
        </button>
      )}
    </Alert>
  );
}

interface InlineWarningProps {
  message: string;
  onDismiss?: () => void;
}

export function InlineWarning({ message, onDismiss }: InlineWarningProps) {
  return (
    <Alert variant="warning" className="flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <AlertDescription className="flex-1">{message}</AlertDescription>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="flex-shrink-0 transition-base text-warning hover:opacity-80"
          aria-label="Dismiss"
        >
          <XCircle className="w-5 h-5" />
        </button>
      )}
    </Alert>
  );
}

